import React, { useState, useEffect } from 'react';
import { useWeb3React } from '@web3-react/core';
import { 
  getPartyAddresses,
  getContractState,
  withdrawInterest,
  withdrawAfterClose,
  withdrawAfterVoid
} from '../utils/contract';

const WithdrawForm = () => {
  const { active, account, library } = useWeb3React();
  const [loading, setLoading] = useState(false);
  const [txResult, setTxResult] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [contractState, setContractState] = useState({ stateNumber: 0, stateName: 'Loading...' });
  const [withdrawOptions, setWithdrawOptions] = useState([]);

  // Determine user role and contract state
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [partyAddresses, state] = await Promise.all([
          getPartyAddresses(),
          getContractState()
        ]);
        
        setContractState(state);
        
        if (active && account) {
          const accountLower = account.toLowerCase();
          if (accountLower === partyAddresses.partyN?.toLowerCase()) {
            setUserRole('N');
          } else if (accountLower === partyAddresses.partyB?.toLowerCase()) {
            setUserRole('B');
          } else if (accountLower === partyAddresses.partyE?.toLowerCase()) {
            setUserRole('E');
          } else {
            setUserRole(null);
          }
        } else {
          setUserRole(null);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchData();
  }, [active, account]);

  // Determine available withdraw options based on user role and contract state
  useEffect(() => {
    const options = [];
    
    if (!userRole || !contractState) return;
    
    // Party B can withdraw interest in ACTIVE state
    if (userRole === 'B' && contractState.stateNumber === 2) { // ACTIVE
      options.push({
        id: 'interest',
        name: 'Withdraw Interest',
        description: 'Withdraw accumulated interest payments',
        action: withdrawInterest
      });
    }
    
    // Party B can withdraw USDC after loan closure
    if (userRole === 'B' && contractState.stateNumber === 4) { // CLOSED
      options.push({
        id: 'afterClose',
        name: 'Withdraw Repaid Loan',
        description: 'Withdraw repaid USDC after loan closure',
        action: withdrawAfterClose
      });
    }
    
    // Party E can withdraw remaining NILA after contract is voided
    if (userRole === 'E' && contractState.stateNumber === 3) { // VOIDED
      options.push({
        id: 'afterVoid',
        name: 'Withdraw Remaining Collateral',
        description: 'Withdraw remaining NILA tokens after contract void',
        action: withdrawAfterVoid
      });
    }
    
    // Party E can withdraw NILA after loan closure
    if (userRole === 'E' && contractState.stateNumber === 4) { // CLOSED
      options.push({
        id: 'afterClose',
        name: 'Withdraw Collateral',
        description: 'Withdraw NILA tokens after loan closure',
        action: withdrawAfterClose
      });
    }
    
    setWithdrawOptions(options);
  }, [userRole, contractState]);

  const handleWithdraw = async (option) => {
    if (!active || !account || !option || !option.action) {
      return;
    }
    
    setLoading(true);
    setTxResult(null);
    
    try {
      const result = await option.action(library, account);
      
      if (result.success) {
        setTxResult({
          success: true,
          message: `${option.name} successful!`,
          hash: result.hash
        });
      } else {
        setTxResult({
          success: false,
          message: `${option.name} failed: ${result.error}`
        });
      }
    } catch (error) {
      console.error('Error in withdraw transaction:', error);
      setTxResult({
        success: false,
        message: `Error: ${error.message || 'Transaction failed'}`
      });
    } finally {
      setLoading(false);
    }
  };

  if (!active || !account) {
    return (
      <div className="withdraw-form">
        <h2>Withdraw</h2>
        <p>Please connect your wallet to withdraw funds.</p>
      </div>
    );
  }

  if (!userRole) {
    return (
      <div className="withdraw-form">
        <h2>Withdraw</h2>
        <p>Your wallet is not authorized to withdraw funds from this contract.</p>
        <p>Only parties N, B, and E can withdraw funds.</p>
      </div>
    );
  }

  if (withdrawOptions.length === 0) {
    return (
      <div className="withdraw-form">
        <h2>Withdraw</h2>
        <p>No withdraw options are currently available for your role (Party {userRole}).</p>
        <p>Contract state: {contractState.stateName}</p>
      </div>
    );
  }

  return (
    <div className="withdraw-form">
      <h2>Withdraw as Party {userRole}</h2>
      
      <div className="info-note">
        <p>
          <strong>Contract State:</strong> {contractState.stateName}
        </p>
        <p>
          <strong>Available Withdraw Options:</strong>
        </p>
      </div>
      
      <div className="withdraw-options">
        {withdrawOptions.map(option => (
          <div key={option.id} className="withdraw-option">
            <h3>{option.name}</h3>
            <p>{option.description}</p>
            <button 
              onClick={() => handleWithdraw(option)} 
              disabled={loading}
              className="submit-button"
            >
              {loading ? 'Processing...' : option.name}
            </button>
          </div>
        ))}
      </div>
      
      {txResult && (
        <div className={`transaction-result ${txResult.success ? 'success' : 'error'}`}>
          <p>{txResult.message}</p>
          {txResult.hash && (
            <a 
              href={`https://testnet.bscscan.com/tx/${txResult.hash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="tx-link"
            >
              View Transaction
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default WithdrawForm;
